import { IMultiYearNodeDTO } from '@modeso/types__aumico-lib-annualreport-be';
import { DeltaClassification } from './row-vm.model';

/** Half a cent — anything below treated as zero. */
export const ZERO_TOLERANCE = 0.005;

/**
 * Placeholder rendered into any cell where a real value can't be computed
 * (non-finite input, division-by-zero, missing data). Deliberately not
 * i18n'd — short visual marker, same in every locale.
 */
export const NO_DATA_PLACEHOLDER = '-';

/** Swiss financial number formatting: apostrophe (’) thousands separator, 2 decimals. */
export function formatNumberSwiss(value: number): string {
    const sign = value < 0 ? '-' : '';
    const abs = Math.abs(value);
    const [intPart, fractionPart = '00'] = abs.toFixed(2).split('.');
    const withSeparators = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '’');
    return `${sign}${withSeparators}.${fractionPart}`;
}

/** Currency cell with `NO_DATA_PLACEHOLDER` ("-") fallback for non-finite values; treats sub-tolerance as zero. */
export function formatCurrency(value: number): string {
    if (!Number.isFinite(value)) return NO_DATA_PLACEHOLDER;
    if (Math.abs(value) < ZERO_TOLERANCE) return formatNumberSwiss(0);
    return formatNumberSwiss(value);
}

/** Signed percentage: leading + / -, two decimals, space before %. */
export function formatPercentageSigned(value: number): string {
    if (!Number.isFinite(value)) return '';
    const sign = value > 0 ? '+' : value < 0 ? '-' : '';
    return `${sign}${Math.abs(value).toFixed(2)} %`;
}

/** Signed CHF with leading + / - and `CHF ` prefix. */
export function formatChfSigned(value: number): string {
    const sign = value > 0 ? '+' : value < 0 ? '-' : '';
    return `${sign}CHF ${formatNumberSwiss(Math.abs(value))}`;
}

/** Signed CHF, *no currency prefix* — for PDF cells where the unit lives in the column header. */
export function formatChfSignedRaw(value: number): string {
    const sign = value > 0 ? '+' : value < 0 ? '-' : '';
    return `${sign}${formatNumberSwiss(Math.abs(value))}`;
}

/**
 * Signed percentage for PDF delta cells — every row carries its own `%`
 * suffix (unlike the BS/IS value columns where the unit lives in the column
 * header). Two decimal places to match the on-screen Verhältniskennzahl
 * formatting.
 */
export function formatPercentageSignedRaw(value: number): string {
    if (!Number.isFinite(value)) return '';
    const sign = value > 0 ? '+' : value < 0 ? '-' : '';
    return `${sign}${Math.abs(value).toFixed(2)} %`;
}

/** Verhältniskennzahl percentage of a node's value against the per-year reference. Two decimals. */
export function formatStructPct(
    value: number,
    reference: number,
    isReferenceNode: boolean,
): string {
    if (isReferenceNode) return '100.00';
    if (Math.abs(reference) < ZERO_TOLERANCE) return NO_DATA_PLACEHOLDER;
    if (!Number.isFinite(value)) return NO_DATA_PLACEHOLDER;
    return ((Math.abs(value) / Math.abs(reference)) * 100).toFixed(2);
}

/** ER row reads as Aufwand (cost) if the sum across all years is negative. */
export function isAufwandNode(node: IMultiYearNodeDTO): boolean {
    if (!node.values || node.values.length === 0) return false;
    const sum = node.values.reduce((acc, v) => acc + (v ?? 0), 0);
    return sum < 0;
}

/** Bilanz nodes never get coloured Δ classifications — they are always neutral. */
export function isBilanzNode(node: IMultiYearNodeDTO): boolean {
    return node.id === 'assets' ||
        node.id === 'liabilities' ||
        node.id.startsWith('assets|') ||
        node.id.startsWith('liabilities|');
}

/**
 * Δ classification:
 *  - Bilanz row → 'neutral'
 *  - Tiny magnitude → 'flat'
 *  - ER Aufwand growing → 'bad'; shrinking → 'good'
 *  - ER Ertrag growing → 'good'; shrinking → 'bad'
 */
export function classifyDelta(magnitudeDelta: number, node: IMultiYearNodeDTO): DeltaClassification {
    if (isBilanzNode(node)) return 'neutral';
    if (Math.abs(magnitudeDelta) < ZERO_TOLERANCE) return 'flat';
    const isAufwand = isAufwandNode(node);
    if (isAufwand) return magnitudeDelta > 0 ? 'bad' : 'good';
    return magnitudeDelta > 0 ? 'good' : 'bad';
}

/** Find a node by its id anywhere in a tree (used for reference-node resolution). */
export function findNodeById(root: IMultiYearNodeDTO | null, id: string): IMultiYearNodeDTO | null {
    if (root == null) return null;
    if (root.id === id) return root;
    for (const c of root.children) {
        const found = findNodeById(c, id);
        if (found != null) return found;
    }
    return null;
}
